import { agentBuildLabel, formatDateTime, shortDigest } from "./onsiteModel";
import type {
  AgentChange,
  AgentReleaseRecord,
  AgentReleases,
  CurrentRelease,
  ReleaseCandidate,
} from "./types";

// Mirrors releaseNoteSchema in apps/platform/src/orpc/routes/admin/onsite/onsite.contract.ts.
const NOTE_MAX = 500;
const SHORT_FINGERPRINT = 12;

export const APPROVER_ONLY =
  "Only an onsite apply approver (ONSITE_APPLY_APPROVERS) can promote or roll back a release.";

const AGENT_CHANGE_TEXT: Record<AgentChange, string> = {
  changed: "Agent changed",
  same: "No agent change",
  unknown: "Agent change unknown",
};

const ACTION_TEXT: Record<string, string> = {
  promote: "Promoted",
  rollback: "Rolled back",
  bootstrap: "Bootstrapped",
};

/** What Promote or Roll back is about to do, held while its dialog is open. */
export type ReleaseIntent =
  | { kind: "promote"; candidate: ReleaseCandidate }
  | { kind: "rollback"; target: AgentReleaseRecord };

export interface ReleaseDialogCopy {
  title: string;
  description: string;
  confirmLabel: string;
  facts: string[];
  behind: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export function agentChangeText(change: AgentChange): string {
  return AGENT_CHANGE_TEXT[change];
}

export function releaseActionText(action: string): string {
  return ACTION_TEXT[action] ?? action;
}

/** "b61bd74ab · 10-23" with a build date, the bare tag without, else the short digest. */
export function releaseBuildLabel(build: {
  digest: string | null;
  tag: string | null;
  builtAt?: string | null;
}): string {
  const { digest, tag, builtAt = null } = build;
  if (tag && builtAt) return agentBuildLabel({ digest, tag, builtAt, fingerprint: null }) ?? tag;
  if (tag) return tag;
  return digest ? shortDigest(digest) : "unknown build";
}

export function shortFingerprint(fingerprint: string | null): string {
  return fingerprint ? fingerprint.slice(0, SHORT_FINGERPRINT) : "—";
}

/** "2 of 5 workspaces will show Behind until setup runs again on each: bk, geo." */
export function behindSummary(names: string[], workspaceCount: number): string {
  if (names.length === 0) return "No workspace will show Behind.";
  const which = names.length === 1 ? "it" : "each";
  const of = `${names.length} of ${plural(workspaceCount, "workspace")}`;
  return `${of} will show Behind until setup runs again on ${which}: ${names.join(", ")}.`;
}

/** Null when the note may be sent; the server requires one, at most 500 characters. */
export function noteError(note: string): string | null {
  if (note.trim() === "") return "A note is required.";
  return note.trim().length > NOTE_MAX ? `Keep the note to ${NOTE_MAX} characters.` : null;
}

/** Why a candidate's Promote button is disabled, or null when it may be clicked. */
export function promoteBlocker(canPromote: boolean, candidate: ReleaseCandidate): string | null {
  return canPromote ? candidate.refusal : APPROVER_ONLY;
}

/** Why "Roll back to previous release" is disabled, or null when it may be clicked. */
export function rollbackBlocker(releases: AgentReleases): string | null {
  return releases.canPromote ? releases.rollback.refusal : APPROVER_ONLY;
}

/** Who made the current release current, or that it is still the env fallback. */
export function currentSourceText(current: CurrentRelease): string {
  const record = current.record;
  if (current.source === "env" || !record) {
    return "ONSITE_AGENT_IMAGE: nothing has been promoted yet. The first promote records it as the bootstrap release.";
  }
  return `${releaseActionText(record.action)} by ${record.promotedBy}, ${formatDateTime(record.promotedAt)}`;
}

function promoteCopy(candidate: ReleaseCandidate, releases: AgentReleases): ReleaseDialogCopy {
  const from = releases.current ? releaseBuildLabel(releases.current) : "the current release";
  return {
    title: `Promote ${releaseBuildLabel(candidate)}?`,
    description:
      "It becomes the release Set up agent and Run setup again install. No workspace changes until setup runs on it.",
    confirmLabel: "Promote",
    facts: [
      `Fingerprint ${shortFingerprint(candidate.fingerprint)}`,
      `${agentChangeText(candidate.agentChange)} from ${from}`,
    ],
    behind: behindSummary(candidate.behindWorkspaces, releases.workspaceCount),
  };
}

function rollbackCopy(target: AgentReleaseRecord, releases: AgentReleases): ReleaseDialogCopy {
  return {
    title: `Roll back to ${releaseBuildLabel(target)}?`,
    description:
      "It becomes the current release again, recorded as a rollback. No workspace changes until setup runs on it.",
    confirmLabel: "Roll back",
    facts: [
      `${releaseActionText(target.action)} by ${target.promotedBy}, ${formatDateTime(target.promotedAt)}`,
      `Fingerprint ${shortFingerprint(target.fingerprint)}`,
    ],
    behind: behindSummary(releases.rollback.behindWorkspaces, releases.workspaceCount),
  };
}

export function releaseDialogCopy(
  intent: ReleaseIntent,
  releases: AgentReleases,
): ReleaseDialogCopy {
  return intent.kind === "promote"
    ? promoteCopy(intent.candidate, releases)
    : rollbackCopy(intent.target, releases);
}

/** Said once a promote or rollback is recorded: what it did, and what installs it. */
export function releaseDoneText(record: AgentReleaseRecord): string {
  const did = record.action === "rollback" ? "Rolled back to" : "Promoted";
  return `${did} ${releaseBuildLabel(record)}. Run setup again on each workspace that shows Behind to install it.`;
}

/** "3 older builds, also newer than the current release, are not shown." */
export function hiddenCandidatesText(hidden: number): string | null {
  if (hidden === 0) return null;
  const verb = hidden === 1 ? "is" : "are";
  return `${plural(hidden, "older build")}, also newer than the current release, ${verb} not shown.`;
}

/** The platform build the "may be newer than the platform" refusal is measured against. */
export function platformLine(platform: AgentReleases["platform"]): string {
  const commit = platform.commit ? platform.commit.slice(0, 9) : "unknown";
  return `Platform commit ${commit}, started ${formatDateTime(platform.startedAt)}. A build can be promoted when it is that commit or was uploaded before the platform started.`;
}
